import rbush from 'rbush';
import * as turf from '@turf/turf';
import type { drawState } from '../../../Types';

export type SnapHitKind = 'vertex' | 'edge';

/** Normalize gl-draw snap options for active sketch / edit modes. */
export function sanitizeSnapOptions(options: any): drawState['options'] {
  const mode = options?.snapMode;
  return {
    snap: Boolean(options?.snap),
    snapMode:
      mode === 'edge' || mode === 'vertex' || mode === 'both' ? mode : 'both',
    snapTolerance:
      typeof options?.snapTolerance === 'number' ? options.snapTolerance : 0,
    snapTolerancePixels:
      typeof options?.snapTolerancePixels === 'number'
        ? options.snapTolerancePixels
        : undefined,
    currentVertexPosition:
      typeof options?.currentVertexPosition === 'number'
        ? options.currentVertexPosition
        : 0,
    rectangle: options?.rectangle ?? null,
  } as drawState['options'];
}

/** Screen-pixel snap radius → meters at the given latitude and zoom. */
export function computeSnapToleranceMetersAt(
  map: any,
  lat: number,
  tolerancePixels: number
): number {
  if (!Number.isFinite(tolerancePixels) || tolerancePixels <= 0) return 0;
  if (!map?.getZoom) return 0;
  const zoom = map.getZoom();
  const metersPerPixel =
    (Math.cos((lat * Math.PI) / 180.0) * 2 * Math.PI * 6378137) /
    (256 * 2 ** zoom);
  const meters = tolerancePixels * metersPerPixel;
  return meters < 99999 ? meters : 99999;
}

function resolveSnapTolerancePixels(state: any, map: any): number {
  if (typeof state?.options?.snapTolerancePixels === 'number') {
    return Math.max(1, state.options.snapTolerancePixels);
  }
  if (typeof map?.drawTools?.options?.snapTolerancePixels === 'number') {
    return Math.max(1, map.drawTools.options.snapTolerancePixels);
  }
  if (typeof map?._snapTolerancePixels === 'number') {
    return Math.max(1, map._snapTolerancePixels);
  }
  return 8;
}

function isValidCoord(coord: unknown): coord is [number, number] {
  return (
    Array.isArray(coord) &&
    coord.length >= 2 &&
    Number.isFinite(coord[0]) &&
    Number.isFinite(coord[1])
  );
}

function isUsableGeometry(geometry: any): boolean {
  if (!geometry?.type || geometry.coordinates == null) return false;
  if (!Array.isArray(geometry.coordinates) || geometry.coordinates.length === 0) {
    return false;
  }
  return true;
}

/** Pixel distance between two lng/lat points on the current map view. */
function pixelDistance(
  map: any,
  a: [number, number],
  b: [number, number]
): number {
  try {
    const pa = map.project(a);
    const pb = map.project(b);
    const dx = pa.x - pb.x;
    const dy = pa.y - pb.y;
    return Math.sqrt(dx * dx + dy * dy);
  } catch {
    return Infinity;
  }
}

/** Search bbox in lng/lat expanded by the pixel snap radius around the cursor. */
function searchBBoxAroundCursor(
  map: any,
  lng: number,
  lat: number,
  tolerancePixels: number
): { minX: number; minY: number; maxX: number; maxY: number } {
  const pad = Math.max(tolerancePixels, 1) + 2;
  try {
    const p = map.project([lng, lat]);
    const sw = map.unproject([p.x - pad, p.y + pad]);
    const ne = map.unproject([p.x + pad, p.y - pad]);
    return {
      minX: Math.min(sw.lng, ne.lng),
      minY: Math.min(sw.lat, ne.lat),
      maxX: Math.max(sw.lng, ne.lng),
      maxY: Math.max(sw.lat, ne.lat),
    };
  } catch {
    const deg = 0.0001;
    return {
      minX: lng - deg,
      minY: lat - deg,
      maxX: lng + deg,
      maxY: lat + deg,
    };
  }
}

function insertFeatureBBox(tree: any, feature: { geometry: any }) {
  if (!isUsableGeometry(feature.geometry)) return;
  try {
    const bbox = turf.bbox(feature.geometry);
    if (!bbox.every(Number.isFinite)) return;
    tree.insert({
      minX: bbox[0],
      minY: bbox[1],
      maxX: bbox[2],
      maxY: bbox[3],
      feature,
    });
  } catch {
    /* skip invalid geometry */
  }
}

function collectPointCoords(geometry: any): [number, number][] {
  if (!geometry) return [];
  if (geometry.type === 'Point' && isValidCoord(geometry.coordinates)) {
    return [[geometry.coordinates[0], geometry.coordinates[1]]];
  }
  if (geometry.type === 'MultiPoint' && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates.filter(isValidCoord).map((c: number[]) => [
      c[0],
      c[1],
    ]);
  }
  return [];
}

function collectLineParts(geometry: any): [number, number][][] {
  if (!geometry) return [];
  if (geometry.type === 'LineString' && Array.isArray(geometry.coordinates)) {
    const part = geometry.coordinates.filter(isValidCoord);
    return part.length >= 2 ? [part] : [];
  }
  if (geometry.type === 'MultiLineString' && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates
      .map((part: unknown) =>
        Array.isArray(part) ? part.filter(isValidCoord) : []
      )
      .filter((part: [number, number][]) => part.length >= 2);
  }
  // Some servers return LineString coords wrapped one level deep.
  if (
    Array.isArray(geometry.coordinates) &&
    geometry.coordinates[0] &&
    Array.isArray(geometry.coordinates[0][0]) &&
    typeof geometry.coordinates[0][0][0] === 'number'
  ) {
    return geometry.coordinates
      .map((part: unknown) =>
        Array.isArray(part) ? part.filter(isValidCoord) : []
      )
      .filter((part: [number, number][]) => part.length >= 2);
  }
  return [];
}

function collectPolygonRings(geometry: any): [number, number][][] {
  if (!geometry) return [];
  if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
    return geometry.coordinates
      .map((ring: unknown) =>
        Array.isArray(ring) ? ring.filter(isValidCoord) : []
      )
      .filter((ring: [number, number][]) => ring.length >= 2);
  }
  if (geometry.type === 'MultiPolygon' && Array.isArray(geometry.coordinates)) {
    const rings: [number, number][][] = [];
    for (const poly of geometry.coordinates) {
      if (!Array.isArray(poly)) continue;
      for (const ring of poly) {
        if (!Array.isArray(ring)) continue;
        const coords = ring.filter(isValidCoord) as [number, number][];
        if (coords.length >= 2) rings.push(coords);
      }
    }
    return rings;
  }
  return [];
}

/** Hide the snap target marker (snap disabled or no hit). */
export function clearSnapMarkerOnMap(map: any): void {
  try {
    const src = map?.getSource?.('geobox-gl-snap-marker');
    const data = src?._data;
    if (!data) return;
    data.features = [
      {
        type: 'Feature',
        properties: { locked: false, kind: null },
        geometry: { type: 'Point', coordinates: [] },
      },
    ];
    src.setData(data);
  } catch {
    /* ignore */
  }
}

/**
 * Update the on-map snap marker.
 * Only show a solid red marker when locked to a vertex — nothing otherwise.
 */
export function setSnapMarkerOnMap(
  map: any,
  lng: number,
  lat: number,
  opts: { locked: boolean; kind?: SnapHitKind | null }
): void {
  try {
    if (!opts.locked || opts.kind !== 'vertex') {
      clearSnapMarkerOnMap(map);
      return;
    }

    const src = map?.getSource?.('geobox-gl-snap-marker');
    if (!src) return;
    const data = src._data ?? {
      type: 'FeatureCollection',
      features: [],
    };
    data.features = [
      {
        type: 'Feature',
        properties: { locked: true, kind: 'vertex' },
        geometry: {
          type: 'Point',
          coordinates: [lng, lat],
        },
      },
    ];
    src.setData(data);
  } catch {
    /* ignore */
  }
}

/**
 * Find the nearest snap target within the pixel tolerance.
 * Returns `[distancePx, coord, kind]`. When no hit, coord/kind are undefined.
 * Also writes `state.options.snapTolerance` as pixels so callers' comparisons stay valid.
 */
export const calculateSnapTolerance = (
  map: any,
  e: any,
  state: any
): [number, number[] | undefined, SnapHitKind | undefined] => {
  const snapPointSource = map.getSource('geobox-gl-snap-point')?._data;
  const snapVertexSource = map.getSource('geobox-gl-snap-vertex')?._data;
  const snapPolygonSource = map.getSource('geobox-gl-snap-polygon')?._data;
  const snapLineSource = map.getSource('geobox-gl-snap-line')?._data;

  if (!snapPointSource || !snapVertexSource || !snapPolygonSource || !snapLineSource) {
    return [Infinity, undefined, undefined];
  }

  const tolerancePixels = resolveSnapTolerancePixels(state, map);
  if (state?.options) {
    // Modes compare minDistance < snapTolerance — keep both in pixels.
    state.options.snapTolerance = tolerancePixels;
    state.options.snapTolerancePixels = tolerancePixels;
  }

  const coords = e.lngLat;
  if (!coords || !Number.isFinite(coords.lng) || !Number.isFinite(coords.lat)) {
    return [Infinity, undefined, undefined];
  }
  const cursor: [number, number] = [coords.lng, coords.lat];
  const searchBox = searchBBoxAroundCursor(
    map,
    cursor[0],
    cursor[1],
    tolerancePixels
  );

  let bestEdgeDist = Infinity;
  let bestEdgeCoord: [number, number] | undefined;
  let bestVertexDist = Infinity;
  let bestVertexCoord: [number, number] | undefined;

  const considerVertex = (coordinate: unknown) => {
    if (!isValidCoord(coordinate)) return;
    const pt: [number, number] = [coordinate[0], coordinate[1]];
    const dist = pixelDistance(map, cursor, pt);
    if (dist < bestVertexDist) {
      bestVertexDist = dist;
      bestVertexCoord = pt;
    }
  };

  const considerEdge = (lineCoords: [number, number][]) => {
    if (lineCoords.length < 2) return;
    try {
      const nearest = turf.nearestPointOnLine(
        turf.lineString(lineCoords),
        turf.point(cursor)
      );
      const pt = nearest.geometry.coordinates as [number, number];
      if (!isValidCoord(pt)) return;
      const dist = pixelDistance(map, cursor, pt);
      if (dist < bestEdgeDist) {
        bestEdgeDist = dist;
        bestEdgeCoord = pt;
      }
    } catch {
      /* ignore bad line */
    }
  };

  const mode = state?.options?.snapMode ?? 'both';

  if (mode === 'both' || mode === 'edge') {
    const pointTree = new rbush();
    for (const feature of snapPointSource.features ?? []) {
      insertFeatureBBox(pointTree, feature);
    }
    for (const item of pointTree.search(searchBox) as any[]) {
      for (const coordinate of collectPointCoords(item.feature.geometry)) {
        // Point features act as vertex targets when edge mode includes points.
        considerVertex(coordinate);
      }
    }

    const lineTree = new rbush();
    for (const feature of snapLineSource.features ?? []) {
      insertFeatureBBox(lineTree, feature);
    }
    for (const item of lineTree.search(searchBox) as any[]) {
      for (const part of collectLineParts(item.feature.geometry)) {
        considerEdge(part);
      }
    }

    const polygonTree = new rbush();
    for (const feature of snapPolygonSource.features ?? []) {
      if (!isUsableGeometry(feature.geometry)) continue;
      try {
        if (feature.geometry.type === 'MultiPolygon') {
          for (const polyCoords of feature.geometry.coordinates) {
            const poly = turf.polygon(polyCoords);
            insertFeatureBBox(polygonTree, poly);
          }
        } else {
          insertFeatureBBox(polygonTree, feature);
        }
      } catch {
        /* skip */
      }
    }
    for (const item of polygonTree.search(searchBox) as any[]) {
      for (const ring of collectPolygonRings(item.feature.geometry)) {
        considerEdge(ring);
      }
    }
  }

  if (mode === 'both' || mode === 'vertex') {
    const vertexTree = new rbush();
    for (const feature of snapVertexSource.features ?? []) {
      insertFeatureBBox(vertexTree, feature);
    }
    for (const item of vertexTree.search(searchBox) as any[]) {
      for (const coordinate of collectPointCoords(item.feature.geometry)) {
        considerVertex(coordinate);
      }
      // Defensive: some vertex features may be plain Point after bad ingest.
      if (item.feature.geometry?.type === 'Point') {
        considerVertex(item.feature.geometry.coordinates);
      }
    }
  }

  // Prefer vertex lock when both are in range (stronger / red feedback).
  if (
    bestVertexCoord &&
    Number.isFinite(bestVertexDist) &&
    bestVertexDist <= tolerancePixels
  ) {
    return [bestVertexDist, bestVertexCoord, 'vertex'];
  }

  if (
    bestEdgeCoord &&
    Number.isFinite(bestEdgeDist) &&
    bestEdgeDist <= tolerancePixels
  ) {
    return [bestEdgeDist, bestEdgeCoord, 'edge'];
  }

  return [Infinity, undefined, undefined];
};
